/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/prefer-for-of */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { ITask, Nullable } from './ITask';
import { getQuery } from 'sutils';
import TaskMonitor from './taskMonitor';
import Thread from '@src/slide/infrastructure/module/dependent/taskManager/thread/thread';
import pureTaskQueue from '@src/slide/infrastructure/module/dependent/taskManager/pure-task-queue';

const forceWorker = getQuery('forceWorker') === '1';

/**
 * 默认execution为同步任务
 * 如果需要支持异步任务
 * 通常情况下只需要继承重写execute方法
 * 等待任务结束后调用finish方法以确保任务正确结束并回收资源
 */
export default class TaskGroup implements ITask {
    readonly key: string;

    // 优先级
    priority: number;

    // 是否正在执行（默认执行过程中的任务不可取消）
    isExecuting: boolean;

    // 是否被取消执行
    isCanceled: boolean;

    isPaused: boolean;

    concurrent: boolean;

    isFinished: boolean;

    waitGroupComplete: boolean;

    enableWorker: boolean;
    performedInWorker: boolean;

    public promise: Promise<any>;

    // worker子线程任务
    workerTaskList: Array<ITask>;

    // 正在执行的任务
    executingTaskMap: Map<string, ITask>;

    waitGroupCompleteTaskArray: Array<ITask>;

    // 最大并发数
    concurrentMax: number;

    completeCallback: Nullable<(task: ITask) => void>;

    executionID: any;
    private resolveFnc: Nullable<(value: unknown) => void>;
    private thread: Thread | undefined;

    constructor(key: string, priority: number, thread?: Thread) {
        this.key = key;
        this.priority = priority;
        this.thread = thread;

        this.reset();
    }

    reset() {
        this.isExecuting = false;
        this.isCanceled = false;
        this.isPaused = false;
        this.isFinished = false;

        this.workerTaskList = [];
        this.executingTaskMap = new Map<string, ITask>();

        this.concurrentMax = (forceWorker || this.thread?.isReady()) ? 30 : 10;

        this.waitGroupCompleteTaskArray = [];

        this.promise = new Promise((resolve) => {
            this.resolveFnc = resolve;
        });
    }

    addTask(task: ITask) {
        if (!task.isFinished && this.workerTaskList.indexOf(task) < 0) {
            this.workerTaskList.push(task);
        }
    }

    getTask(taskId: string) {
        return this.workerTaskList.find(e => e.key === taskId);
    }

    getTaskCount() {
        return this.workerTaskList.length;
    }

    public asyncStart() {
        if (!this.isExecuting) {
            TaskMonitor.getInstance().addMarkBegin(`${this.key}(Performance)`);
        }

        this.executionID = pureTaskQueue.add(() => {
            this.execute();
        });
    }

    public start() {
        this.execute();
    }

    execute() {
        if (this.workerTaskList.length > 0) {
            if (!this.isExecuting) {
                this.isExecuting = true;
                // window.log.info(`[taskGroup] ${this.key}`);
            }
            this.performOneFrame();
        } else if (this.executingTaskMap.size <= 0) {
            this.finish();
        }
    }

    handleTaskFinished = (task: ITask) => {
        this.executingTaskMap.delete(task.key);
        if (task.waitGroupComplete) {
            this.waitGroupCompleteTaskArray.push(task);
        } else {
            if (!this.isCanceled) {
                task.notifyComplete();
            }
        }

        // 停止执行
        if (this.executingTaskMap.size <= 0) {
            if (this.workerTaskList.length <= 0) {
                this.finish();
            } else {
                this.asyncStart();
            }
        }
    };

    executeInTaskList(taskList: Array<ITask>, concurrentMax: number) {
        const executeCount = Math.min(concurrentMax, taskList.length);
        const executeArray = taskList.splice(0, executeCount);
        executeArray.forEach((task) => {
            this.executingTaskMap.set(task.key, task);
            task.start();
            task.promise.then(() => {
                this.handleTaskFinished(task);
            });
        });
    }

    performOneFrame() {
        TaskMonitor.getInstance().addMarkBegin(`${this.key}(TaskGroupFrame)`);

        if (this.workerTaskList.length > 0) {
            this.executeInTaskList(this.workerTaskList, this.concurrentMax);
        }

        // 开启下一次循环(子线程的渲染不会产生新的任务，所以这要在这里保持任务处理循环即可)
        this.asyncStart();

        TaskMonitor.getInstance().addMarkEnd(`${this.key}(TaskGroupFrame)`);
    }

    finish() {
        this.isExecuting = false;
        this.isFinished = true;

        // 判断停止原因
        if (!this.isCanceled && !this.isPaused) {
            // 任务执行完成则callback
            requestAnimationFrame(() => {
                for (let i = 0; i < this.waitGroupCompleteTaskArray.length; i++) {
                    const task = this.waitGroupCompleteTaskArray[i];
                    task.notifyComplete();
                }
                this.waitGroupCompleteTaskArray = [];
            });
        }

        this.executionID && pureTaskQueue.remove(this.executionID);
        this.resolveFnc?.(true);

        TaskMonitor.getInstance().addMarkEnd(`${this.key}(Performance)`);
    }

    cancel() {
        if (!this.isExecuting) {
            this.isCanceled = true;
            this.finish();
        }
    }

    pause() {
        if (!this.isExecuting) {
            this.isPaused = true;
            this.finish();
        }
    }

    resume() {
        this.isPaused = false;
    }

    public notifyComplete() {
        this.completeCallback?.(this);
    }
}
